/**
 * Host clue-rating panel (#201, FR-11.x; D201.10/D201.11/D201.15).
 *
 * A **host-only** annotation surface that lives in a distinct panel **below the board** —
 * never on a board cell / question card (owner: on-card taps mis-fire on mobile) and never
 * on the player or presentation view (D201.5/D201.11). It targets the just-resolved clue at
 * the top and lists earlier completed clues to rate. Each clue rates in one action → one
 * `POST /api/clue-ratings` (FR-9.5/9.6): 👍 Good posts immediately; 👎 Bad reveals the fixed
 * "why?" reason chips (D201.10) and Save/Skip each post once. The saved confirmation is
 * announced to assistive tech (`role="status"`/`aria-live` — D201.6). Rendered only when the
 * host enabled rating at create time and NOT in auto-host mode (D201.12), gated by the caller.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RATING_REASONS, type GameState, type RatingReason } from '@jeopardy/shared';
import { categoryNameForClue } from '../game/selectors';
import { rateClue, unrateClue } from '../net/clue-ratings-api';
import { ApiError } from '../net/api-error';

/**
 * How long the "Rating saved" confirmation + its Undo affordance linger before the rated clue
 * is removed from the panel (#201 fix-request). The clue is removed sooner when the host opens
 * the next clue. Undo within this window keeps the clue in the list, rateable again.
 */
const RATING_BANNER_MS = 5000;

/** Human-readable labels for the fixed reason-chip set (mock 08). */
const REASON_LABELS: Readonly<Record<RatingReason, string>> = {
  'self-revealing': 'Self-revealing',
  ambiguous: 'Ambiguous',
  'wrong-answer': 'Wrong answer',
  'too-easy': 'Too easy',
  'too-hard': 'Too hard',
  other: 'Other',
};

/** A completed clue the host can rate (derived from the board's consumed cells). */
interface CompletedClue {
  clueId: string;
  category: string;
  value: number;
  prompt: string;
  response: string;
}

/**
 * The completed (consumed) clues on the current board, most-recently-resolved first — the
 * order `consumedClueIds` is appended in. Only clues with a resolvable category + text are
 * included (a rating snapshot must be complete). Exported for unit testing the derivation.
 */
export function completedCluesForRating(state: GameState): CompletedClue[] {
  const out: CompletedClue[] = [];
  for (const clueId of [...state.jeopardy.board.consumedClueIds].reverse()) {
    const clue = state.jeopardy.packSnapshot.cluesById[clueId];
    const category = categoryNameForClue(state, clueId);
    if (!clue || !category) continue;
    out.push({
      clueId,
      category,
      value: clue.value,
      prompt: clue.prompt,
      response: clue.response,
    });
  }
  return out;
}

interface ClueRatingProps {
  state: GameState;
  /** The host ticket authorizing ratings for this game. */
  ticket: string;
  /** The host's signed guest identity (rater attribution is resolved server-side). */
  guestToken: string;
}

type RowStatus = 'idle' | 'choosing-reasons' | 'saving' | 'saved' | 'error';

/** One rateable completed clue. Owns its own verdict/reason/submit UI state. */
function ClueRatingRow({
  clue,
  gameId,
  ticket,
  guestToken,
  primary,
  activeClueId,
  onDismiss,
}: {
  clue: CompletedClue;
  gameId: string;
  ticket: string;
  guestToken: string;
  primary: boolean;
  /** The clue currently open on the board, if any — used to remove a rated clue early. */
  activeClueId: string | undefined;
  /** Called once the rated clue's undo window closes so the panel removes it from the DOM. */
  onDismiss: (clueId: string) => void;
}): React.JSX.Element {
  const [status, setStatus] = useState<RowStatus>('idle');
  const [reasons, setReasons] = useState<Set<RatingReason>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Once rated, the clue lingers with its "Rating saved" banner + Undo for a brief window, then
  // is removed from the panel entirely (#201 fix-request) — it does NOT revert to a rateable
  // row. The timer is armed only while `saved`; the cleanup clears it on Undo (status leaves
  // `saved`) or on unmount, so it never fires against a stale row.
  useEffect(() => {
    if (status !== 'saved') return;
    const timer = setTimeout(() => onDismiss(clue.clueId), RATING_BANNER_MS);
    return () => clearTimeout(timer);
  }, [status, onDismiss, clue.clueId]);

  // Opening/selecting another clue removes an already-rated clue right away — don't wait out
  // the 5s (#201 fix-request). A ref (not a dep) so this fires only on an actual clue change,
  // reading the latest saved-ness without re-arming on every status transition.
  const savedRef = useRef(false);
  savedRef.current = status === 'saved';
  useEffect(() => {
    if (savedRef.current) onDismiss(clue.clueId);
  }, [activeClueId, onDismiss, clue.clueId]);

  async function submit(
    verdict: 'good' | 'bad',
    withReasons: RatingReason[],
  ): Promise<void> {
    setStatus('saving');
    setError(null);
    try {
      await rateClue(
        {
          gameId,
          category: clue.category,
          prompt: clue.prompt,
          response: clue.response,
          verdict,
          ...(verdict === 'bad' ? { reasons: withReasons } : {}),
          ticket,
        },
        guestToken,
      );
      setStatus('saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that rating.');
      setStatus('error');
    }
  }

  async function undo(): Promise<void> {
    // Hide the banner immediately (optimistic), then retract the persisted row so the table
    // stays consistent with the UI. If the delete fails the row is already idle, so the host
    // can simply re-rate — the write is an idempotent overwrite, so no orphan state lingers.
    setStatus('idle');
    try {
      await unrateClue(
        { gameId, category: clue.category, prompt: clue.prompt, ticket },
        guestToken,
      );
    } catch {
      // Best-effort retract; nothing actionable to surface once the banner is gone.
    }
  }

  function toggleReason(reason: RatingReason): void {
    setReasons((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) next.delete(reason);
      else next.add(reason);
      return next;
    });
  }

  // Preserve the canonical order when submitting the chosen reasons.
  const orderedReasons = (): RatingReason[] =>
    RATING_REASONS.filter((r) => reasons.has(r));

  if (status === 'saved') {
    return (
      <div className="rate-row rate-row--saved" data-testid={`rate-saved-${clue.clueId}`}>
        <div className="thanks" role="status" aria-live="polite">
          <span className="thanks__check" aria-hidden="true">
            ✓
          </span>
          <div>
            <div className="thanks__title">Rating saved</div>
            <div className="thanks__sub">
              Thanks — this clue is now part of the ground-truth set.
            </div>
          </div>
          <button
            type="button"
            className="btn btn--link thanks__undo"
            data-testid={`rate-undo-${clue.clueId}`}
            onClick={() => void undo()}
          >
            Undo
          </button>
        </div>
      </div>
    );
  }

  const clueLabel = (
    <p className="rate-row__clue">
      <b>
        {clue.category} — ${clue.value}:
      </b>{' '}
      “{clue.prompt}” <span className="muted">— Answer: {clue.response}</span>
    </p>
  );

  return (
    <div
      className={'rate-row' + (primary ? ' rate-row--primary' : '')}
      data-testid={`rate-row-${clue.clueId}`}
    >
      {clueLabel}
      <div className="verdict">
        <button
          type="button"
          className="btn btn--correct"
          data-testid={`rate-good-${clue.clueId}`}
          disabled={status === 'saving'}
          onClick={() => void submit('good', [])}
        >
          👍 Good
        </button>
        <button
          type="button"
          className="btn btn--incorrect"
          aria-expanded={status === 'choosing-reasons'}
          data-testid={`rate-bad-${clue.clueId}`}
          disabled={status === 'saving'}
          onClick={() => setStatus('choosing-reasons')}
        >
          👎 Bad
        </button>
      </div>

      {status === 'choosing-reasons' && (
        <div className="rate-row__reasons">
          <p className="reasons__label">
            Why? <span className="muted">(optional — helps train generation)</span>
          </p>
          <div className="reasons" role="group" aria-label="Reason">
            {RATING_REASONS.map((reason) => {
              const on = reasons.has(reason);
              return (
                <button
                  key={reason}
                  type="button"
                  className={'chip' + (on ? ' chip--on' : '')}
                  aria-pressed={on}
                  data-testid={`reason-${reason}-${clue.clueId}`}
                  onClick={() => toggleReason(reason)}
                >
                  {REASON_LABELS[reason]}
                </button>
              );
            })}
          </div>
          <div className="controls">
            <button
              type="button"
              className="btn btn--primary"
              data-testid={`rate-save-${clue.clueId}`}
              onClick={() => void submit('bad', orderedReasons())}
            >
              Save rating
            </button>
            <button
              type="button"
              className="btn btn--link"
              data-testid={`rate-skip-${clue.clueId}`}
              onClick={() => void submit('bad', [])}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {status === 'error' && error && (
        <p className="error-text" role="alert" data-testid={`rate-error-${clue.clueId}`}>
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The below-board host rating panel. Renders nothing when there are no completed clues yet
 * (before any cell resolves there is nothing to rate). The caller gates on the host-local
 * enable flag + non-auto-host mode (D201.12).
 */
export function ClueRating({
  state,
  ticket,
  guestToken,
}: ClueRatingProps): React.JSX.Element | null {
  // Clues the host has rated (and whose undo window has closed) are removed from the panel and
  // never reappear for the rest of the session (#201 fix-request). Kept here, above the rows,
  // so removal survives the row unmounting.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const onDismiss = useCallback(
    (clueId: string) =>
      setDismissed((prev) => (prev.has(clueId) ? prev : new Set(prev).add(clueId))),
    [],
  );

  const completed = completedCluesForRating(state);
  const visible = completed.filter((clue) => !dismissed.has(clue.clueId));
  if (visible.length === 0) return null;
  return (
    <div
      className="rate-card"
      role="group"
      aria-label="Rate a clue"
      data-testid="clue-rating"
    >
      <p className="rate-card__q">Rate a clue</p>
      <p className="rate-card__hint">
        Rate the clue that just returned to the board, or any earlier completed clue.
        Host-only — players never see these ratings.
      </p>
      {visible.map((clue, i) => (
        <ClueRatingRow
          key={clue.clueId}
          clue={clue}
          gameId={state.gameId}
          ticket={ticket}
          guestToken={guestToken}
          primary={i === 0}
          activeClueId={state.jeopardy.clueState?.clueId}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}
